import { ENUM_LABELS, type ImportRunView } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Button } from '#client/components/ui/Button';
import { Card } from '#client/components/ui/Card';
import { Progress } from '#client/components/ui/Progress';
import { importUrls } from '#client/lib/endpoints';
import { routes } from '#client/lib/routes';
import type { ImportRunState } from '../useImportRun';
import { formatCount } from '#client/lib/format';
import { ButtonLink } from '#client/components/ui/ButtonLink';

/**
 * Step four: write it.
 *
 * The only irreversible action in the wizard, so it says plainly what it is
 * about to do and does not hide the two facts that matter afterwards — that a
 * partly-finished import is a real state, and that the rows which failed can be
 * downloaded, fixed and re-uploaded rather than the whole file being run again.
 */
export function CommitStep({ state }: { state: ImportRunState }) {
  const { run, isBusy, commit } = state;
  if (!run) return null;

  if (run.status === 'committed' || run.status === 'partially_committed') {
    return <CommitDone run={run} />;
  }
  return <CommitPending run={run} isBusy={isBusy} onCommit={() => void commit()} />;
}

/** The write has finished, fully or in part. */
function CommitDone({ run }: { run: ImportRunView }) {
  return (
    <div className="c-form-stack">
      <Banner tone={run.status === 'committed' ? 'success' : 'warning'}>
        {run.status === 'committed'
          ? `All ${formatCount(run.committed_rows)} rows were imported.`
          : `${formatCount(run.committed_rows)} rows were imported and ${run.failed_rows} could not be written. The ones that landed are in the system — do not re-run the whole file.`}
      </Banner>

      <Card title="Done">
        <dl className="c-summary">
          <div className="c-summary__row">
            <dt>Imported</dt>
            <dd>{formatCount(run.committed_rows)}</dd>
          </div>
          {run.failed_rows > 0 && (
            <div className="c-summary__row">
              <dt>Could not be written</dt>
              <dd>{formatCount(run.failed_rows)}</dd>
            </div>
          )}
          <div className="c-summary__row">
            <dt>Skipped</dt>
            <dd>{formatCount(run.skipped_rows)}</dd>
          </div>
        </dl>

        <div className="c-form-actions">
          {run.failed_rows > 0 && (
            <a
              className="c-button c-button--secondary c-button--md"
              href={importUrls.errors(run.id)}
            >
              Download the rows that failed
            </a>
          )}
          <ButtonLink variant="primary" size="md" to={routes.imports}>
            Back to imports
          </ButtonLink>
        </div>
      </Card>
    </div>
  );
}

/** Not yet written, or being written now. */
function CommitPending({
  run,
  isBusy,
  onCommit,
}: {
  run: ImportRunView;
  isBusy: boolean;
  onCommit: () => void;
}) {
  const writable = run.ok_rows + run.warning_rows;
  const running = run.status === 'committing';

  return (
    <div className="c-form-stack">
      {run.error_message && <Banner tone="critical">{run.error_message}</Banner>}

      <Card
        title={running ? 'Importing' : 'Ready to import'}
        subtitle={`${ENUM_LABELS.importEntity[run.entity]} · ${run.file_name}`}
      >
        {running ? (
          <>
            <Progress entered={run.committed_rows} total={writable} />
            <p className="c-field__help">
              Writing in batches. You can leave this page — it carries on without the browser, and
              the import history will show how it finished.
            </p>
          </>
        ) : (
          <>
            <dl className="c-summary">
              <div className="c-summary__row c-summary__row--total">
                <dt>Rows that will be written</dt>
                <dd>{formatCount(writable)}</dd>
              </div>
              {run.error_rows > 0 && (
                <div className="c-summary__row">
                  <dt>Left out — needing attention</dt>
                  <dd>{formatCount(run.error_rows)}</dd>
                </div>
              )}
            </dl>

            {run.error_rows > 0 && (
              <p className="c-field__help">
                The rows needing attention are skipped, not blocked: the rest still import, and you
                can fix those and upload them on their own afterwards.
              </p>
            )}

            <div className="c-form-actions">
              <Button
                variant="primary"
                onClick={onCommit}
                loading={isBusy}
                disabled={writable === 0}
              >
                Import {formatCount(writable)} rows
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
